import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { useTranslation } from "react-i18next";

const BoxChart = ({ data, tripRte, tripDir, inbound }) => {
  const [t] = useTranslation("common");

  const chartRef = useRef();
  const containerRef = useRef();

  const [size, setSize] = useState({ width: 0, height: 0 });

  let tooltip = d3.select(chartRef.current.parentElement).select(".tooltipboxchart");

  if (tooltip.empty()) {
    tooltip = d3
      .select(chartRef.current.parentElement)
      .append("div")
      .attr("class", "tooltipboxchart");
  }

  // Observe container size
  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      if (!entries.length) return;
      const { width, height } = entries[0].contentRect;
      setSize({ width, height });
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Draw chart
  useEffect(() => {
    const { width, height } = size;
    if (!width || !height || !data?.length) return;

    const margins = { top: 20, right: 20, bottom: 70, left: 40 };
    const innerWidth = width - margins.left - margins.right;
    const innerHeight = height - margins.top - margins.bottom;

    const svg = d3.select(chartRef.current);
    svg.selectAll("*").remove(); // clean redraw

    svg
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("width", "100%")
      .style("height", "100%")
      .style("font", "10px sans-serif");

    // -------- DATA PREP --------

    const tripIds = Array.from(new Set(data.map((d) => d.trip_id)));

    const thresholds = tripIds
      .map((id) => {
        const row = data.find((d) => d.trip_id === id);
        return +row.plannedstarttime;
      })
      .sort((a, b) => a - b);

    const bins = d3
      .bin()
      .thresholds(thresholds)
      .value((d) => +d.plannedstarttime)(data)
      .map((bin) => {
        bin.sort((a, b) => a.realduration - b.realduration);

        const values = bin.map((d) => d.realduration);
        const min = values[0];
        const max = values[values.length - 1];

        const q1 = d3.quantile(values, 0.25);
        const q2 = d3.quantile(values, 0.5);
        const q3 = d3.quantile(values, 0.75);

        const iqr = q3 - q1;
        const r0 = Math.max(min, q1 - iqr * 1.5);
        const r1 = Math.min(max, q3 + iqr * 1.5);

        bin.quartiles = [q1, q2, q3];
        bin.range = [r0, r1];

        bin.outliers = bin.filter(
          (v) => v.realduration < r0 || v.realduration > r1,
        );

        bin.plannedDuration = d3.mean(bin.map((d) => d.plannedduration));
        bin.plannedStart = d3.mean(bin.map((d) => d.plannedstarttime));

        return bin;
      });

    // -------- SCALES --------

    const x = d3
      .scaleLinear()
      .domain([d3.min(bins, (d) => d.x0), d3.max(bins, (d) => d.x1)])
      .range([margins.left, width - margins.right]);

    const y = d3
      .scaleLinear()
      .domain([0, d3.max(bins, (d) => d.range[1])])
      .nice()
      .range([height - margins.bottom, margins.top]);

    const formatTime = (d) => {
      const date = new Date(1970, 0, 1, 0, 0, d);
      return d3.timeFormat("%H:%M:%S")(date);
    };

    const tickValues = bins.map((bin) => (bin.x0 + bin.x1) / 2);
    const tickLabels = bins.map((bin) => formatTime(bin.plannedStart));

    // -------- DRAW --------

    const g = svg.append("g").selectAll("g").data(bins).join("g");

    const maxWidth = 40;

    // Box
    g.append("path")
      .attr("fill", "#ddd")
      .attr("d", (d) => {
        const x0 = x(d.x0) + 1;
        const x1 = x(d.x1);

        const fullWidth = x1 - x0;
        const boxWidth = Math.min(fullWidth, maxWidth);

        const left = x0 + (fullWidth - boxWidth) / 2;
        const right = left + boxWidth;

        return `
      M${left},${y(d.quartiles[2])}
      H${right}
      V${y(d.quartiles[0])}
      H${left}
      Z
    `;
      })
      .on("mouseover", (event, d) => {
        const xPos = event.pageX;
        const yPos = event.pageY;

        tooltip
          .html(
            `
      Min : ${(d.range[0] / 60).toFixed(0)}<br/>
      1st quartile : ${(d.quartiles[0] / 60).toFixed(0)}<br/>
      Median : ${(d.quartiles[1] / 60).toFixed(0)}<br/>
      Planned : ${(d.plannedDuration / 60).toFixed(0)}<br/>
      3rd quartile : ${(d.quartiles[2] / 60).toFixed(0)}<br/>
      Max : ${(d.range[1] / 60).toFixed(0)}
    `,
          )
          .style("position", "absolute")
          .style("left", xPos + 10 + "px")
          .style("top", yPos - 40 + "px")
          .style("opacity", 0.9);
      })
      .on("mouseout", () => {
        tooltip.style("opacity", 0);
      });

    // Median
    g.append("path")
      .attr("stroke", "black")
      .attr("stroke-width", 2)
      .attr("d", (d) => {
        const x0 = x(d.x0) + 1;
        const x1 = x(d.x1);

        const fullWidth = x1 - x0;
        const boxWidth = Math.min(fullWidth, maxWidth);

        const left = x0 + (fullWidth - boxWidth) / 2;
        const right = left + boxWidth;

        return `M${left},${y(d.quartiles[1])} H${right}`;
      })
      .on("mouseover", (event, d) => {
        const xPos = event.pageX;
        const yPos = event.pageY;

        tooltip
          .html(
            `
      Min : ${(d.range[0] / 60).toFixed(0)}<br/>
      1st quartile : ${(d.quartiles[0] / 60).toFixed(0)}<br/>
      Median : ${(d.quartiles[1] / 60).toFixed(0)}<br/>
      Planned : ${(d.plannedDuration / 60).toFixed(0)}<br/>
      3rd quartile : ${(d.quartiles[2] / 60).toFixed(0)}<br/>
      Max : ${(d.range[1] / 60).toFixed(0)}
    `,
          )
          .style("position", "absolute")
          .style("left", xPos + 10 + "px")
          .style("top", yPos - 40 + "px")
          .style("opacity", 0.9);
      })
      .on("mouseout", () => {
        tooltip.style("opacity", 0);
      });

    // Whiskers
    g.append("line")
      .attr("stroke", "black")
      .attr("x1", (d) => x((d.x0 + d.x1) / 2))
      .attr("x2", (d) => x((d.x0 + d.x1) / 2))
      .attr("y1", (d) => y(d.range[0]))
      .attr("y2", (d) => y(d.range[1]));

    // Outliers
    g.append("g")
      .attr("transform", (d) => `translate(${x((d.x0 + d.x1) / 2)},0)`)
      .selectAll("circle")
      .data((d) => d.outliers)
      .join("circle")
      .attr("r", 2)
      .attr("cx", () => (Math.random() - 0.5) * 6)
      .attr("cy", (d) => y(d.realduration))
      .attr("fill", "black")
      .attr("opacity", 0.3);

    // Planned dots
    svg
      .selectAll(".planned-dot")
      .data(bins)
      .join("circle")
      .attr("class", "planned-dot")
      .attr("cx", (d) => x((d.x0 + d.x1) / 2))
      .attr("cy", (d) => y(d.plannedDuration))
      .attr("r", 3)
      .attr("fill", "red");

    // X axis
    svg
      .append("g")
      .attr("transform", `translate(0,${height - margins.bottom})`)
      .call(
        d3
          .axisBottom(x)
          .tickValues(tickValues)
          .tickFormat((d, i) => tickLabels[i]),
      )
      .selectAll("text")
      .attr("transform", "rotate(-90)")
      .attr("text-anchor", "end")
      .attr("dx", "-0.8em")
      .attr("dy", "-0.5em");

    // Y axis
    svg
      .append("g")
      .attr("transform", `translate(${margins.left},0)`)
      .call(d3.axisLeft(y).tickFormat((d) => (d / 60).toFixed(0)))
      .call((g) => g.select(".domain").remove());
  }, [data, size]);

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100%" }}>
      <svg ref={chartRef}></svg>
    </div>
  );
};

export default BoxChart;
